import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Plus, Minus, Star, ChevronRight, Edit, Trash2, MessageCircle, LayoutGrid, List, User, ArrowLeft, ArrowRight, Sparkles, Bot, X, Copy, Check } from "lucide-react";
import { useState } from "react";
import { type AnalysisItem } from "../types";
import { getCurrentCsrfToken, getCsrfToken } from "../services/csrfService";
import { analyticsService } from "../services/analyticsService";
import { useTranslation } from "react-i18next";
import { parseArgumentSelectionLogic } from "../utils/argumentSelectionLogic";

interface ProsConsV3Props {
  pros: AnalysisItem[];
  cons: AnalysisItem[];
  layoutMode?: 'single' | 'double';
  onLayoutChange?: (mode: 'single' | 'double') => void;
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
  onAddCustomArgument: (type: 'pro' | 'con', text: string, rating: number) => void;
  onMoveArgument: (argumentId: string, fromType: 'pro' | 'con') => void;
  onUpdateArgumentRating: (argumentId: string, newRating: number, type: 'pro' | 'con') => void;
  onDeleteArgument?: (argumentId: string) => void;
  onOpenAIPrompt?: (item: AnalysisItem) => void;
  isReadOnly?: boolean;
  analysisId?: string;
  analysisQuestion?: string;
  analysisDescription?: string;
  existingItems?: AnalysisItem[];
  shareId?: string;
  isTransitioning?: boolean;
  transitionDelay?: number;
  argumentSelectionLogic?: string | null; // Пояснение принципа отбора аргументов
  plusListTitle?: string | null; // Заголовок для списка плюсов
  minusListTitle?: string | null; // Заголовок для списка минусов
}

function StarRating({ 
  rating, 
  maxRating, 
  color, 
  theme, 
  onRatingChange, 
  interactive = false 
}: { 
  rating: number; 
  maxRating: number; 
  color: string; 
  theme: 'dark' | 'light';
  onRatingChange?: (newRating: number) => void;
  interactive?: boolean;
}) {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const isGreen = color === 'green';
  const isDark = theme === 'dark';
  const displayRating = hoveredRating !== null ? hoveredRating : rating;
  
  return (
    <div 
      className="flex items-center gap-1"
      onMouseLeave={() => setHoveredRating(null)}
    >
      {Array.from({ length: maxRating }, (_, i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${
            i < displayRating 
              ? isGreen 
                ? 'fill-green-400 text-green-400' 
                : 'fill-red-400 text-red-400'
              : isDark ? 'text-slate-600' : 'text-gray-400'
          } ${
            interactive 
              ? 'cursor-pointer hover:scale-110 transition-all duration-150' 
              : ''
          } ${
            interactive && hoveredRating !== null && i < hoveredRating
              ? 'drop-shadow-sm'
              : ''
          }`}
          onMouseEnter={interactive ? () => setHoveredRating(i + 1) : undefined}
          onClick={interactive && onRatingChange ? () => onRatingChange(i + 1) : undefined}
        />
      ))}
      <span className={`text-xs ml-1 transition-all duration-150 ${
        hoveredRating !== null && interactive
          ? isDark ? 'text-slate-300 font-medium' : 'text-gray-700 font-medium'
          : isDark ? 'text-slate-400' : 'text-gray-600'
      }`}>
        {displayRating}/{maxRating}
      </span>
    </div>
  );
}

function FactorCard({ 
  item, 
  type, 
  index,
  theme,
  language,
  onMoveArgument,
  onUpdateArgumentRating,
  onDeleteArgument,
  onOpenAIPrompt,
  isReadOnly = false,
  shareId
}: { 
  item: AnalysisItem; 
  type: 'pro' | 'con';
  index: number;
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
  onMoveArgument: (argumentId: string, fromType: 'pro' | 'con') => void;
  onUpdateArgumentRating: (argumentId: string, newRating: number, type: 'pro' | 'con') => void;
  onDeleteArgument?: (argumentId: string) => void;
  onOpenAIPrompt?: (item: AnalysisItem) => void;
  isReadOnly?: boolean;
  shareId?: string;
}) {
  const { t } = useTranslation('analysis');
  const isPositive = type === 'pro';
  const percentage = (item.rating / 5) * 100;
  const isDark = theme === 'dark';
  
  return (
    <div className="w-full max-w-full">
      <Card className={`group p-5 ${
        isDark 
          ? 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600/50 hover:bg-slate-800/50' 
          : 'bg-white/80 border-gray-200/60 hover:border-gray-300/60 hover:bg-white shadow-lg hover:shadow-xl'
      } ${item.isModified ? (isDark ? 'border-orange-500/50 bg-orange-500/10' : 'border-orange-300 bg-orange-50') : ''}`}>
      <div className="flex items-start gap-4">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold ${
          isPositive 
            ? 'bg-gradient-to-br from-green-500/20 to-emerald-600/20 text-green-400 border border-green-500/20' 
            : 'bg-gradient-to-br from-red-500/20 to-rose-600/20 text-red-400 border border-red-500/20'
        }`}>
          {item.isModified ? (
            <Edit className="w-4 h-4" />
          ) : (
            index + 1
          )}
        </div>
        
        <div className="flex-1 space-y-3 min-w-0">
          <p className={`leading-relaxed break-words text-sm sm:text-base ${
            isDark ? 'text-slate-200' : 'text-gray-900'
          }`}>{item.text}</p>
          
          <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap min-w-0 flex-1">
              <StarRating 
                rating={item.rating} 
                maxRating={5} 
                color={isPositive ? 'green' : 'red'} 
                theme={theme}
                interactive={!isReadOnly}
                onRatingChange={!isReadOnly ? (newRating) => onUpdateArgumentRating(item.id, newRating, type) : undefined}
              />
              <Badge 
                variant="outline" 
                className={`text-xs flex-shrink-0 ${
                  isPositive 
                    ? 'border-green-500/30 text-green-400 bg-green-500/10' 
                    : 'border-red-500/30 text-red-400 bg-red-500/10'
                }`}
              >
                {percentage.toFixed(0)}%
              </Badge>
              {item.isModified && (
                <Badge 
                  variant="outline" 
                  className={`text-xs flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${
                    isDark 
                      ? 'border-orange-500/30 text-orange-400 bg-orange-500/10' 
                      : 'border-orange-500/30 text-orange-600 bg-orange-500/10'
                  }`}
                  title={shareId ? t('modified') : t('modifiedByYou')}
                >
                  <Edit className="w-3 h-3 flex-shrink-0" />
                  <span className="hidden sm:inline">
                    {shareId ? t('modified') : t('modifiedByYou')}
                  </span>
                  <span className="sm:hidden">
                    {t('modified')}
                  </span>
                </Badge>
              )}
            </div>
            
            {(!isReadOnly || onOpenAIPrompt) && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
                {onOpenAIPrompt && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`h-8 w-8 p-0 transition-colors ${
                      isDark 
                        ? 'text-slate-400 hover:text-blue-400 hover:bg-blue-500/10' 
                        : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                    onClick={() => onOpenAIPrompt(item)}
                  >
                    <MessageCircle className="w-3 h-3" />
                  </Button>
                )}
                {!isReadOnly && (
                  <>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => onMoveArgument(item.id, type)}
                      className={`h-8 w-8 p-0 transition-colors ${
                        isDark 
                          ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-600/20' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title={type === 'pro' 
                        ? (t('moveToCons')) 
                        : (t('moveToPros'))
                      }
                    >
                      {type === 'pro' ? (
                        <ArrowRight className="w-3 h-3" />
                      ) : (
                        <ArrowLeft className="w-3 h-3" />
                      )}
                    </Button>
                    {onDeleteArgument && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className={`h-8 w-8 p-0 transition-colors ${
                          isDark 
                            ? 'text-slate-400 hover:text-red-400 hover:bg-red-500/10' 
                            : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                        }`}
                        onClick={() => onDeleteArgument(item.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Progress bar */}
          <div className={`w-full h-1 rounded-full overflow-hidden ${
            isDark ? 'bg-slate-700' : 'bg-gray-200'
          }`}>
            <div 
              className={`h-full rounded-full ${
                isPositive 
                  ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                  : 'bg-gradient-to-r from-red-400 to-rose-500'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      </div>
    </Card>
    </div>
  );
}

function AddArgumentCard({ 
  type, 
  language, 
  theme, 
  onAdd,
  analysisId,
  analysisQuestion,
  analysisDescription,
  existingItems = []
}: { 
  type: 'pro' | 'con'; 
  language: 'ru' | 'en'; 
  theme: 'dark' | 'light'; 
  onAdd: (text: string, rating: number) => void;
  analysisId?: string;
  analysisQuestion?: string;
  analysisDescription?: string;
  existingItems?: AnalysisItem[];
}) {
  const { t } = useTranslation('analysis');
  const [addMode, setAddMode] = useState<'none' | 'choice' | 'manual' | 'ai-generating' | 'ai-generated'>('none');
  const [text, setText] = useState('');
  const [rating, setRating] = useState<number>(3);
  const [, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const MAX_TEXT_LENGTH = 200;
  const currentLength = text.length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || currentLength > MAX_TEXT_LENGTH) return;
    
    onAdd(text.trim(), rating);
    setText('');
    setRating(3);
    setAddMode('none');
  };

  const handleChooseManual = () => {
    setAddMode('manual');
    setText('');
    setRating(3);
    setGenerationError(null);
  };

  const handleChooseAI = () => {
    setAddMode('ai-generating');
    handleGenerateWithAI();
  };

  const handleCancel = () => {
    setText('');
    setRating(3);
    setAddMode('none');
    setGenerationError(null);
  };

  // Функция для генерации аргумента с помощью ИИ
  const handleGenerateWithAI = async () => {
    if (!analysisId || !analysisDescription) {
      console.error('Недостаточно данных для генерации аргумента');
      return;
    }
    
    // Очищаем предыдущие ошибки
    setGenerationError(null);
    
    // Используем question или title как fallback
    const questionText = analysisQuestion || 'Анализ решения';

    setIsGenerating(true);
    
    // Определяем URL share-server
    const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';
    
    try {
      // Получаем CSRF токен
      let csrfToken = getCurrentCsrfToken();
      if (!csrfToken) {
        console.log('CSRF: Токен не найден, запрашиваем новый');
        csrfToken = await getCsrfToken();
      } else {
        console.log('CSRF: Используем существующий токен из кэша');
      }

      // Используем существующий API для расширения анализа, но генерируем только один аргумент
      const response = await fetch(`${SHARE_SERVER_URL}/api/expand-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include', // Важно для CSRF
        body: JSON.stringify({
          externalId: analysisId, // Используем externalId как в analysisService
          plusCount: type === 'pro' ? 1 : 0,
          minusCount: type === 'con' ? 1 : 0,
          currentAnalysis: {
            question: questionText,
            description: analysisDescription,
            items: existingItems // Передаем существующие аргументы для избежания дублирования
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.newItems && data.newItems.length > 0) {
        // Берем первый сгенерированный аргумент
        const generatedItem = data.newItems[0];
        setText(generatedItem.text);
        setRating(generatedItem.rating);
        setAddMode('ai-generated');
      } else {
        setGenerationError(t('failedToGenerateArgument'));
        setAddMode('choice'); // Возвращаемся к выбору способа
      }
    } catch (error) {
      console.error('Ошибка при генерации аргумента:', error);
      setGenerationError(t('errorGeneratingArgument'));
      setAddMode('choice'); // Возвращаемся к выбору способа
    } finally {
      setIsGenerating(false);
    }
  };

  const isDark = theme === 'dark';
  const isPositive = type === 'pro';

  // Состояние "none" - кнопка добавления
  if (addMode === 'none') {
    return (
      <Card 
        className={`group transition-all duration-300 p-5 border-2 border-dashed cursor-pointer hover:scale-[1.02] ${
        isDark 
          ? 'bg-slate-800/30 border-slate-600/50 hover:border-slate-500/70 hover:bg-slate-800/50' 
          : 'bg-white/80 border-gray-300/60 hover:border-gray-400/70 hover:bg-white shadow-md hover:shadow-lg'
      }`} onClick={() => {
        analyticsService.trackEvent(isPositive ? 'add-positive-arg' : 'add-negative-arg');
        setAddMode('choice');
      }}>
        <div className="flex flex-col items-center justify-center space-y-3 text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
            isPositive 
              ? 'bg-gradient-to-br from-green-500/20 to-emerald-600/20 text-green-400 border-2 border-green-500/30' 
              : 'bg-gradient-to-br from-red-500/20 to-rose-600/20 text-red-400 border-2 border-red-500/30'
          }`}>
            <Plus className="w-6 h-6" />
          </div>
          
          <div>
            <h4 className={`font-medium ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {t('addArgument')}
            </h4>
            <p className={`text-sm ${
              isDark ? 'text-slate-400' : 'text-gray-600'
            }`}>
              {t('clickToAdd')}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Состояние "choice" - выбор способа добавления
  if (addMode === 'choice') {
    return (
      <Card className={`transition-all duration-300 p-5 ${
        isDark 
          ? 'bg-slate-800/50 border-slate-700' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className={`font-medium ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {t('howToAddArgument')}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className={`h-8 w-8 p-0 ${
                isDark 
                  ? 'text-gray-400 hover:text-white hover:bg-slate-700' 
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Кнопка "Написать самому" */}
            <Button
              variant="outline"
              onClick={handleChooseManual}
              className={`h-auto p-4 flex flex-col items-center space-y-2 transition-all duration-200 hover:scale-[1.02] group ${
                isDark 
                  ? 'border-slate-600 bg-slate-800/50 text-slate-200 hover:bg-slate-700/70 hover:border-slate-500 hover:text-white' 
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 shadow-sm hover:shadow-md'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                isDark 
                  ? 'bg-blue-500/20 text-blue-400 group-hover:bg-blue-500/30 group-hover:text-blue-300'
                  : 'bg-blue-500/10 text-blue-600 group-hover:bg-blue-500/20 group-hover:text-blue-700'
              }`}>
                <User className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium">
                {t('writeManually')}
              </span>
              <span className={`text-xs text-center leading-tight ${
                isDark ? 'text-slate-400 group-hover:text-slate-300' : 'text-gray-500 group-hover:text-gray-600'
              }`}>
                {t('createYourOwnArgument')}
              </span>
            </Button>

            {/* Кнопка "Сгенерировать ИИ" */}
            <Button
              variant="outline"
              onClick={handleChooseAI}
              disabled={!analysisId}
              className={`h-auto p-4 flex flex-col items-center space-y-2 transition-all duration-200 hover:scale-[1.02] group ${
                isDark 
                  ? 'border-slate-600 bg-slate-800/50 text-slate-200 hover:bg-slate-700/70 hover:border-slate-500 hover:text-white' 
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 shadow-sm hover:shadow-md'
              } ${!analysisId ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                isDark 
                  ? 'bg-purple-500/20 text-purple-400 group-hover:bg-purple-500/30 group-hover:text-purple-300'
                  : 'bg-purple-500/10 text-purple-600 group-hover:bg-purple-500/20 group-hover:text-purple-700'
              }`}>
                <Sparkles className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium">
                {t('generateWithAI')}
              </span>
              <span className={`text-xs text-center leading-tight ${
                isDark ? 'text-slate-400 group-hover:text-slate-300' : 'text-gray-500 group-hover:text-gray-600'
              }`}>
                {t('smartGeneration')}
              </span>
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Состояние "ai-generating" - прелоадер генерации
  if (addMode === 'ai-generating') {
    return (
      <Card className={`transition-all duration-300 p-5 ${
        isDark 
          ? 'bg-slate-800/50 border-slate-700' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className={`w-5 h-5 ${
                isPositive ? 'text-green-400' : 'text-red-400'
              }`} />
              <h4 className={`font-medium ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {t('aiGeneration')}
              </h4>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAddMode('choice')}
              className={`h-8 w-8 p-0 ${
                isDark 
                  ? 'text-gray-400 hover:text-white hover:bg-slate-700' 
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="text-center py-8">
            <div className="flex flex-col items-center space-y-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isPositive 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                <Bot className="w-6 h-6 animate-spin" />
              </div>
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('generatingArgument')}
                </p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  {t('pleaseWait')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Состояния "manual" и "ai-generated" - форма добавления
  return (
    <Card className={`transition-all duration-300 p-5 ${
      isDark 
        ? 'bg-slate-800/50 border-slate-700' 
        : 'bg-gray-50 border-gray-200'
    }`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {addMode === 'ai-generated' ? (
              <Sparkles className={`w-5 h-5 ${
                isPositive ? 'text-green-400' : 'text-red-400'
              }`} />
            ) : (
              <User className={`w-5 h-5 ${
                isPositive ? 'text-green-400' : 'text-red-400'
              }`} />
            )}
            <h4 className={`font-medium ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {addMode === 'ai-generated' 
                ? t('aiGeneration')
                : (type === 'pro' ? t('addYourOwnPro') : t('addYourOwnCon'))
              }
            </h4>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setAddMode('choice')}
            className={`h-8 w-8 p-0 ${
              isDark 
                ? 'text-gray-400 hover:text-white hover:bg-slate-700' 
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('enterYourArgument')}
            className={`w-full px-3 py-2 rounded-md border resize-none ${
              isDark 
                ? `bg-slate-700 text-white placeholder-gray-400 ${
                    currentLength > MAX_TEXT_LENGTH ? 'border-red-500' : 'border-slate-600'
                  }`
                : `bg-white text-gray-900 placeholder-gray-500 ${
                    currentLength > MAX_TEXT_LENGTH ? 'border-red-300' : 'border-gray-300'
                  }`
            }`}
            rows={4}
            required
            maxLength={MAX_TEXT_LENGTH}
          />
          <div className="mt-1 flex justify-end">
            <span className={`text-xs ${
              currentLength > MAX_TEXT_LENGTH 
                ? 'text-red-600' 
                : isDark 
                  ? 'text-gray-400' 
                  : 'text-gray-500'
            }`}>
              {currentLength > MAX_TEXT_LENGTH 
                ? t('characterLimitExceeded')
                : `${currentLength}/${MAX_TEXT_LENGTH} ${t('characters')}`
              }
            </span>
          </div>
        </div>

        <div>
          <label className={`block mb-2 text-sm ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {t('importanceRating')}
          </label>
          <select 
            value={rating} 
            onChange={(e) => setRating(parseInt(e.target.value))}
            className={`w-full px-3 py-2 rounded-md border ${
              isDark 
                ? 'bg-slate-700 border-slate-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value={1}>1 - {t('low')}</option>
            <option value={2}>2 - {t('belowAverage')}</option>
            <option value={3}>3 - {t('average')}</option>
            <option value={4}>4 - {t('high')}</option>
            <option value={5}>5 - {t('veryHigh')}</option>
          </select>
        </div>

        {/* Сообщение об ошибке генерации */}
        {generationError && (
          <div className={`p-3 rounded-md ${
            isDark 
              ? 'bg-red-900/50 border border-red-500/30 text-red-400' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {generationError}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={!text.trim()}
            className={`flex-1 ${
              isPositive 
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('save')}
          </Button>
          {addMode === 'ai-generated' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setText('');
                setRating(3);
                setAddMode('ai-generating');
                handleGenerateWithAI();
              }}
              className={`${
                isDark 
                  ? 'border-slate-600 text-gray-300 hover:bg-slate-700' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('regenerate')}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className={`${
                isDark 
                  ? 'border-slate-600 text-gray-300 hover:bg-slate-700' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('cancel')}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}

function FactorSection({ 
  title, 
  items, 
  type, 
  icon: Icon, 
  gradientFrom,
  gradientTo,
  textColor,
  borderColor,
  theme,
  language,
  onAddCustomArgument,
  onMoveArgument,
  onUpdateArgumentRating,
  onDeleteArgument,
  onOpenAIPrompt,
  isReadOnly = false,
  analysisId,
  analysisQuestion,
  analysisDescription,
  existingItems = [],
  shareId,
  isTransitioning = false,
  transitionDelay = 0
}: {
  title: string;
  items: AnalysisItem[];
  type: 'pro' | 'con';
  icon: React.ComponentType<{ className?: string }>;
  gradientFrom: string;
  gradientTo: string;
  textColor: string;
  borderColor: string;
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
  onAddCustomArgument: (type: 'pro' | 'con', text: string, rating: number) => void;
  onMoveArgument: (argumentId: string, fromType: 'pro' | 'con') => void;
  onUpdateArgumentRating: (argumentId: string, newRating: number, type: 'pro' | 'con') => void;
  onDeleteArgument?: (argumentId: string) => void;
  onOpenAIPrompt?: (item: AnalysisItem) => void;
  isReadOnly?: boolean;
  analysisId?: string;
  analysisQuestion?: string;
  analysisDescription?: string;
  existingItems?: AnalysisItem[];
  shareId?: string;
  isTransitioning?: boolean;
  transitionDelay?: number;
}) {
  const { t } = useTranslation('analysis');
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const totalRating = items.reduce((sum, item) => sum + item.rating, 0);

  const handleAddArgument = (text: string, rating: number) => {
    onAddCustomArgument(type, text, rating);
  };

  const handleCopyAllArguments = async () => {
    if (items.length === 0) return;
    
    // Вычисляем суммарную оценку
    const totalScore = items.reduce((sum, item) => sum + item.rating, 0);
    
    // Определяем заголовок в зависимости от типа столбца
    const header = type === 'pro' ? t('positiveFactors') : t('negativeFactors');
    
    // Формируем текст для копирования: каждый аргумент с оценкой на новой строке
    const argumentsText = items.map((item, index) => 
      `${index + 1}. ${item.text} (${t('rating')}: ${item.rating})`
    ).join('\n');
    
    // Добавляем суммарную оценку отдельной строкой
    const totalScoreLabel = t('totalScore');
    const textToCopy = `${header}\n${argumentsText}\n\n${totalScoreLabel}: ${totalScore}`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Ошибка при копировании:', error);
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error('Ошибка при копировании (fallback):', err);
      }
      document.body.removeChild(textArea);
    }
  };
  
  return (
    <div className="space-y-4 w-full max-w-full">
      <Card className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} border-0 p-6 relative overflow-hidden w-full max-w-full`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }} />
        </div>
        
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-white/10 backdrop-blur-sm border ${borderColor}`}>
              <Icon className={`w-7 h-7 ${textColor}`} />
            </div>
            
            <div>
              <h3 className={`text-xl sm:text-2xl font-semibold ${textColor}`}>{title}</h3>
              <p className={theme === 'dark' ? 'text-white/70' : 'text-gray-700'}>
                {items.length} {t('factors')} • {t('totalScoreLabel')}: {totalRating}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleCopyAllArguments}
                className={`w-8 h-8 p-0 rounded-lg backdrop-blur-sm border transition-all duration-200 ${
                  theme === 'dark' 
                    ? isCopied
                      ? 'text-green-400 bg-green-500/20 border-green-500/30'
                      : 'text-white/60 hover:text-white bg-white/5 border-white/10 hover:bg-white/15 hover:border-white/25'
                    : isCopied
                      ? 'text-green-600 bg-green-500/20 border-green-500/30'
                      : 'text-gray-600 hover:text-gray-900 bg-white/5 border-white/10 hover:bg-white/15 hover:border-white/25'
                }`}
                title={isCopied ? t('copied') : t('copyAllArguments')}
              >
                {isCopied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            )}
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className={`w-8 h-8 p-0 rounded-lg backdrop-blur-sm border transition-all duration-200 ${
                theme === 'dark' 
                  ? 'text-white/60 hover:text-white bg-white/5 border-white/10 hover:bg-white/15 hover:border-white/25'
                  : 'text-gray-600 hover:text-gray-900 bg-white/5 border-white/10 hover:bg-white/15 hover:border-white/25'
              }`}
            >
              <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`} />
            </Button>
          </div>
        </div>
      </Card>
      
      {isExpanded && (
        <div className="space-y-3 w-full max-w-full">
          {items.map((item, index) => (
            <FactorCard 
              key={item.id} 
              item={item} 
              type={type}
              index={index}
              theme={theme}
              language={language}
              onMoveArgument={onMoveArgument}
              onUpdateArgumentRating={onUpdateArgumentRating}
              onDeleteArgument={onDeleteArgument}
              onOpenAIPrompt={onOpenAIPrompt}
              isReadOnly={isReadOnly}
              shareId={shareId}
            />
          ))}
          
          {!isReadOnly && (
            <div className="pt-1 pb-2">
              <AddArgumentCard
                type={type}
                language={language}
                theme={theme}
                onAdd={handleAddArgument}
                analysisId={analysisId}
                analysisQuestion={analysisQuestion}
                analysisDescription={analysisDescription}
                existingItems={existingItems}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LayoutToggle({ 
  layoutMode, 
  onLayoutChange,
  theme,
  language 
}: { 
  layoutMode: 'single' | 'double'; 
  onLayoutChange: (mode: 'single' | 'double') => void; 
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
}) {
  const { t } = useTranslation('analysis');
  const isDark = theme === 'dark';
  
  return (
    <div className={`flex items-center gap-1 backdrop-blur-sm rounded-lg p-1 border ${
      isDark 
        ? 'bg-slate-800/50 border-slate-700/50' 
        : 'bg-white/80 border-gray-200/60 shadow-lg'
    }`}>
      <Button
        variant={layoutMode === 'single' ? 'default' : 'ghost'}
        size="sm"
        onClick={() => onLayoutChange('single')}
        className={`h-8 w-8 p-0 ${
          layoutMode === 'single'
            ? isDark 
              ? 'bg-slate-700 text-white shadow-sm'
              : 'bg-gray-200 text-gray-900 shadow-sm'
            : isDark
              ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
        }`}
        title={t('oneColumn')}
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        variant={layoutMode === 'double' ? 'default' : 'ghost'}
        size="sm"
        onClick={() => onLayoutChange('double')}
        className={`h-8 w-8 p-0 ${
          layoutMode === 'double'
            ? isDark 
              ? 'bg-slate-700 text-white shadow-sm'
              : 'bg-gray-200 text-gray-900 shadow-sm'
            : isDark
              ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
        }`}
        title={t('twoColumns')}
      >
        <LayoutGrid className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function ProsConsV3({ 
  pros, 
  cons, 
  layoutMode = 'single', 
  onLayoutChange,
  theme,
  language,
  onAddCustomArgument,
  onMoveArgument,
  onUpdateArgumentRating,
  onDeleteArgument,
  onOpenAIPrompt,
  isReadOnly = false,
  analysisId,
  analysisQuestion,
  analysisDescription,
  existingItems = [],
  shareId,
  isTransitioning = false,
  transitionDelay = 0,
  argumentSelectionLogic,
  plusListTitle,
  minusListTitle
}: ProsConsV3Props) {
  const { t } = useTranslation('analysis');
  const isDark = theme === 'dark';
  
  return (
    <div className="space-y-8 w-full max-w-full">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="text-center lg:text-left space-y-4">
          <h2 className={`heading-section text-2xl sm:text-3xl ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {t('detailedAnalysis')}
          </h2>
          <p className={`text-base sm:text-lg md:text-xl ${
            isDark ? 'text-slate-300' : 'text-gray-700'
          }`}>
            {argumentSelectionLogic && argumentSelectionLogic.trim() ? (
              <>
                {t('argumentSelectionIntro')}{' '}
                {parseArgumentSelectionLogic(argumentSelectionLogic).map((seg, i) => {
                  if (seg.type === 'text') return <span key={i}>{seg.content}</span>;
                  if (seg.type === 'plus') {
                    return (
                      <span
                        key={i}
                        className={`font-medium underline decoration-dashed underline-offset-4 ${isDark
                          ? 'text-emerald-400 decoration-emerald-400'
                          : 'text-emerald-600 decoration-emerald-600'}`}
                      >
                        {seg.content}
                      </span>
                    );
                  }
                  return (
                    <span
                      key={i}
                      className={`font-medium underline decoration-dashed underline-offset-4 ${isDark
                        ? 'text-amber-400 decoration-amber-400'
                        : 'text-amber-600 decoration-amber-600'}`}
                    >
                      {seg.content}
                    </span>
                  );
                })}
              </>
            ) : (
              t('argumentSelectionIntro')
            )}
          </p>
        </div>
        
        {onLayoutChange && (
          <LayoutToggle 
            layoutMode={layoutMode} 
            onLayoutChange={onLayoutChange}
            theme={theme}
            language={language}
          />
        )}
      </div>
      
      <div className={`grid gap-8 w-full max-w-full ${
        layoutMode === 'double' ? 'lg:grid-cols-2' : 'lg:grid-cols-1'
      }`}>
        <FactorSection
          title={(plusListTitle && plusListTitle.trim()) || t('positiveFactors')}
          items={pros}
          type="pro"
          icon={Plus}
          gradientFrom="from-green-500/20"
          gradientTo="to-emerald-600/20"
          textColor="text-green-400"
          borderColor="border-green-500/30"
          theme={theme}
          language={language}
          onAddCustomArgument={onAddCustomArgument}
          onMoveArgument={onMoveArgument}
          onUpdateArgumentRating={onUpdateArgumentRating}
          onDeleteArgument={onDeleteArgument}
          onOpenAIPrompt={onOpenAIPrompt}
          isReadOnly={isReadOnly}
          analysisId={analysisId}
          analysisQuestion={analysisQuestion}
          analysisDescription={analysisDescription}
          existingItems={existingItems}
          shareId={shareId}
          isTransitioning={isTransitioning}
          transitionDelay={transitionDelay}
        />
        
        <FactorSection
          title={(minusListTitle && minusListTitle.trim()) || t('negativeFactors')}
          items={cons}
          type="con"
          icon={Minus}
          gradientFrom="from-red-500/20"
          gradientTo="to-rose-600/20"
          textColor="text-red-400"
          borderColor="border-red-500/30"
          theme={theme}
          language={language}
          onAddCustomArgument={onAddCustomArgument}
          onMoveArgument={onMoveArgument}
          onUpdateArgumentRating={onUpdateArgumentRating}
          onDeleteArgument={onDeleteArgument}
          onOpenAIPrompt={onOpenAIPrompt}
          isReadOnly={isReadOnly}
          analysisId={analysisId}
          analysisQuestion={analysisQuestion}
          analysisDescription={analysisDescription}
          existingItems={existingItems}
          shareId={shareId}
          isTransitioning={isTransitioning}
          transitionDelay={transitionDelay + 0.05}
        />
      </div>
    </div>
  );
}
